import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import {
  Validators,
  FormControl,
  FormGroup,
  FormBuilder,
} from '@angular/forms';
import { UserService } from '../../../../services/user.service';
import { CyclistService } from '../../../../services/cyclist.service';
import { PartnerService } from '../../../../services/partner.service';
import { TechnicianService } from '../../../../services/technician.service';
import { HelpersService } from '../../../../services/helpers.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
})
export class UserComponent implements OnInit {
  @Input() user;
  @Input() partnerId;
  @Input() cyclistView: boolean;
  @Input() messageService: MessageService;
  @Input() technicians; //Use this to when linking a technician to see if they exist before recreating them.
  @Output() update = new EventEmitter();

  public UserForm: FormGroup;
  public genders: any = [
    { label: this.translate.instant('general.male'), value: 1 },
    { label: this.translate.instant('general.female'), value: 2 },
  ];
  public busy: boolean = false;
  public error: boolean = false;
  public errorMessage = '';
  public message = '';
  public userExists: boolean = false;
  public userLinkedToPartner: boolean = false;
  public userToLink: any;
  public isTechnician: boolean = false;
  public isSuperUser: boolean = false;
  public DefaultUnitOfMeasure = 1;
  public userView: string = null;

  public showGDPRMarketingConsent: boolean = false;

  constructor(
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    public helpersService: HelpersService,
    public userService: UserService,
    public cyclistService: CyclistService,
    public partnerService: PartnerService,
    public technicianService: TechnicianService,
    public modal: NgbActiveModal,
    private translate: TranslateService,
  ) {
    var partnerData = helpersService.getPartnerData();
    this.DefaultUnitOfMeasure =
      partnerData && partnerData.DefaultUnitOfMeasure
        ? partnerData.DefaultUnitOfMeasure
        : 1;

    this.showGDPRMarketingConsent = partnerData.ShowGDPRMarketingConsent;
  }

  ngOnInit() {
    this.setupValidation();
    if (this.user.ID && this.user.TechnicianID > 0) {
      this.isTechnician = true;
    }

    this.isSuperUser = this.helpersService.getProfileData().IsSuperUser;
  }

  setupValidation() {
    if (!this.user.ID) {
      this.UserForm = this.fb.group({
        Name: new FormControl(this.user.Name, Validators.required),
        Surname: new FormControl(this.user.Surname, Validators.required),

        Email: new FormControl(this.user.Email, [
          Validators.required,
          Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$'),
        ]),
        PhoneNumber: new FormControl(
          this.user.PhoneNumber,
          Validators.nullValidator,
        ),
        DateOfBirth: new FormControl(
          moment(this.user.DateOfBirth).toDate(),
          Validators.nullValidator,
        ),
        GenderID: new FormControl(this.user.GenderID, Validators.nullValidator),
        Password: new FormControl('', Validators.required),
        ConfirmPassword: new FormControl('', Validators.required),
      });
    } else {
      this.UserForm = this.fb.group({
        Name: new FormControl(this.user.Name, Validators.required),
        Surname: new FormControl(this.user.Surname, Validators.required),
        Email: new FormControl(this.user.Email, [
          Validators.required,
          Validators.email,
        ]),
        PhoneNumber: new FormControl(
          this.user.PhoneNumber,
          Validators.nullValidator,
        ),
        DateOfBirth: new FormControl(
          this.user.DateOfBirth
            ? moment(this.user.DateOfBirth).toDate()
            : this.user.DateOfBirth,
          Validators.nullValidator,
        ),
        GenderID: new FormControl(this.user.GenderID, Validators.nullValidator),
        UserGaveGDPRMarketingConsent: new FormControl(
          this.user.GDPRMarketingConsent || false,
          Validators.nullValidator,
        ),
      });
    }
  }

  reloadControlData() {
    this.UserForm.controls['Name'].setValue(this.user.Name);
    this.UserForm.controls['Surname'].setValue(this.user.Surname);
    this.UserForm.controls['PhoneNumber'].setValue(this.user.PhoneNumber);
    this.UserForm.controls['DateOfBirth'].setValue(
      moment(this.user.DateOfBirth).toDate(),
    );
    this.UserForm.controls['GenderID'].setValue(this.user.GenderID);
  }

  disableControls() {
    this.UserForm.controls['Name'].disable();
    this.UserForm.controls['Surname'].disable();
    this.UserForm.controls['PhoneNumber'].disable();
    this.UserForm.controls['DateOfBirth'].disable();
    this.UserForm.controls['GenderID'].disable();
    if (!this.user.ID) {
      this.UserForm.controls['Password'].disable();
      this.UserForm.controls['ConfirmPassword'].disable();
    }
  }

  enableControls() {
    this.UserForm.controls['Name'].enable();
    this.UserForm.controls['Surname'].enable();
    this.UserForm.controls['PhoneNumber'].enable();
    this.UserForm.controls['DateOfBirth'].enable();
    this.UserForm.controls['GenderID'].enable();
    if (!this.user.ID) {
      this.UserForm.controls['Password'].enable();
      this.UserForm.controls['ConfirmPassword'].enable();
    }
  }

  searchUser(event) {
    //Only want to do the search on new users (Who won't have a user ID yet)
    if (!this.user.ID) {
      this.cyclistService
        .getCyclist(this.UserForm.controls['Email'].value)
        .subscribe((searchResults) => {
          //User exists, disable and show relavant action
          if (searchResults && searchResults.length > 0) {
            this.userExists = true;
            this.userToLink = {};

            this.disableControls();

            if (searchResults[0].PartnerID > 0) {
              //User exists and is linked to a partner
              this.userLinkedToPartner = true;
              this.error = true;
              if (this.partnerId !== searchResults[0].PartnerID) {
                this.errorMessage = this.translate.instant(
                  'messages.user_exists_linked_to_different_bikeshop',
                );
              } else {
                this.errorMessage = this.translate.instant(
                  'messages.user_exists_linked_to_bikeshop',
                );
              }
            } else {
              //User exists but is not linked to a partner
              this.userLinkedToPartner = false;
              this.errorMessage = this.translate.instant(
                'messages.user_with_email_exists',
              );
              this.error = true;
              this.userToLink = searchResults[0];
              //this.linkUser.toggle(event);
              this.confirmationService.confirm({
                key: 'user_key',
                header: this.translate.instant('general.confirm'),
                acceptLabel: this.translate.instant('general.yes'),
                rejectLabel: this.translate.instant('general.no'),
                message: this.translate.instant(
                  'messages.user_exists_link_to_bikeshop',
                  { username: this.userToLink.UserName },
                ),
                accept: () => {
                  this.linkUserToPartner();
                },
              });
            }
          } else {
            //Allow user to be linked.
            this.userExists = false;
            this.userLinkedToPartner = false;
            this.error = false;
            this.enableControls();
          }
        });
    }
  }

  linkUserToPartner() {
    this.cyclistService
      .getCyclistById(this.userToLink.ID)
      .subscribe((cyclist) => {
        cyclist.PartnerID = this.partnerId;
        cyclist.Partner = null;
        cyclist.IsPartnerUser = true;
        cyclist.ShowCyclistSite = true;
        cyclist.IsImpersonating = false;
        cyclist.Salutation =
          cyclist.Name + ' ' + cyclist.Surname + ' (' + cyclist.UserName + ')';
        cyclist.IsStravaLinked_Description = cyclist.IsStravaLinked
          ? 'Linked'
          : 'Not Linked';
        cyclist.Messages = [];
        cyclist.UnitsOfMeasureID = this.DefaultUnitOfMeasure;
        cyclist.PartnerID = this.partnerId;
        this.busy = true;
        this.cyclistService.saveCyclist(cyclist).subscribe((user) => {
          this.messageService.add({
            life: 5000,
            detail: this.translate.instant('messages.user_linked_to_shop', {
              name: this.userToLink.Name,
            }),
          });
          this.user = user;
          this.enableControls();
          this.reloadControlData();
          this.userExists = false;
          this.userLinkedToPartner = false;
          this.error = false;
          this.busy = false;
          if (this.userView === 'technician_view') {
            this.makeTechnician();
          }
        });
      });
  }

  unlinkUser() {
    this.confirmationService.confirm({
      key: 'user_key',
      header: this.translate.instant('general.confirm'),
      acceptLabel: this.translate.instant('general.yes'),
      rejectLabel: this.translate.instant('general.no'),
      message: this.translate.instant('messages.unlink_user_confirmation'),
      accept: () => {
        this.cyclistService
          .getCyclistById(this.user.ID)
          .subscribe((cyclist) => {
            cyclist.PartnerID = 0;
            cyclist.Partner = null;
            cyclist.IsPartnerUser = false;
            cyclist.ShowCyclistSite = true;
            cyclist.IsImpersonating = false;
            cyclist.Salutation =
              cyclist.Name +
              ' ' +
              cyclist.Surname +
              ' (' +
              cyclist.UserName +
              ')';
            cyclist.IsStravaLinked_Description = cyclist.IsStravaLinked
              ? 'Linked'
              : 'Not Linked';
            cyclist.Messages = [];
            cyclist.PartnerID = this.partnerId;
            this.busy = true;
            this.cyclistService.saveCyclist(cyclist).subscribe((result) => {
              this.modal.close();
              this.messageService.add({
                life: 5000,
                detail: this.translate.instant(
                  'messages.user_unlinked_from_shop',
                  { name: this.user.Name },
                ),
              });
            });
          });
      },
    });
  }

  saveUser(formData) {
    if (this.user.ID) {
      this.updateUser(formData);
    } else {
      this.createUser(formData);
    }
  }

  updateUser(formData) {
    let cyclist = this.user;
    cyclist.UserName = formData.Email;
    cyclist.Email = formData.Email;
    cyclist.Name = formData.Name;
    cyclist.Surname = formData.Surname;
    cyclist.PhoneNumber = formData.PhoneNumber;
    cyclist.DateOfBirth = moment(formData.DateOfBirth)
      .endOf('day')
      .toISOString();
    cyclist.GenderID = formData.GenderID == null ? 0 : formData.GenderID;
    cyclist.GDPRMarketingConsent = formData.UserGaveGDPRMarketingConsent;
    cyclist.IsPartnerUser = true;
    cyclist.ShowCyclistSite = true;
    cyclist.IsImpersonating = false;
    cyclist.PartnerID = this.partnerId;
    cyclist.Salutation =
      cyclist.Name + ' ' + cyclist.Surname + ' (' + cyclist.UserName + ')';
    cyclist.IsStravaLinked_Description = cyclist.IsStravaLinked
      ? 'Linked'  : 'Not Linked';
    cyclist.Messages = [];
    this.busy = true;
    this.userService.updateCustomer(cyclist).subscribe(
      (user) => {
        this.messageService.add({
          life: 5000,
          detail: this.translate.instant('messages.user_updated', {
            name: cyclist.Name,
          }),
        });
        this.busy = false;
      },
      (err) => {
        if(err.error && err.error.error && err.error.error === "CUSTOMER_EXISTS") {
          this.messageService.add({ life: 5000, detail: this.translate.instant('bikeshop.customer.error.customer_exists') });
        }else{
          this.messageService.add({ life: 5000, detail: this.translate.instant('bikeshop.customer.error.customer_general_error') });
        }      
        this.busy = false;
      }, 
    );
  }

  createUser(formData) {
    let user = formData;
    user.Username = user.Email;
    user.PartnerID = this.partnerId;
    user.DateOfBirth = moment(user.DateOfBirth).endOf('day').toISOString();
    user.PartnerID = this.partnerId;
    this.busy = true;
    this.error = false;
    this.userService.createCustomer(user).subscribe((newUser) => {
     // this.cyclistService.linkCyclists(this.partnerId, newUser).subscribe(linkedUser => {
        this.busy = false;
     //   this.userToLink = newUser;
     //   this.linkUserToPartner();
     // });
      
    }); 
  }

  actionTechnician() {
    if (!this.isTechnician) {
      this.confirmMakeTechnician();
    } else {
      this.delinkTechnician();
    }
  }

  confirmMakeTechnician() {
    this.confirmationService.confirm({
      key: 'user_key',
      header: this.translate.instant('general.confirm'),
      acceptLabel: this.translate.instant('general.yes'),
      rejectLabel: this.translate.instant('general.no'),
      message: this.translate.instant('messages.technician_view_confirmation'),
      accept: () => {
        this.makeTechnician();
      },
      reject: () => {
        this.isTechnician = false;
      },
    });
  }

  makeTechnician() {
    var technician = this.technicians
      .filter((technician) => {
        return (
          this.UserForm.controls['Name'].value === technician.Firstname &&
          this.UserForm.controls['Surname'].value === technician.Surname
        );
      })
      .pop();

    if (technician) {
      technician.UserID = this.user.ID;
      technician.IsActive = true;
      this.technicianService.saveTechnician(technician).subscribe(
        (technician) => {
          if (technician.ID) {
            this.user.TechnicianID = technician.ID;
            this.user.Technician = technician;
            this.update.emit(this.user);
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant('messages.technician_linked', {
                name: technician.Firstname,
              }),
            });
            this.isTechnician = true;
            console.log(this.userView);
            if (this.userView === 'technician_view') {
              this.message = this.translate.instant(
                'messages.add_working_hours_reminder',
              );
            }
          }
        },
        (err) => {
          this.error = true;
          this.errorMessage =
            'You have reached you limit for adding technicians. You can make other technician inactive or upgrade your plan';
        },
      );
    } else {
      technician = {
        Firstname: this.user.Name,
        Surname: this.user.Surname,
        HoursPerDay: 8,
        IsActive: true,
        PartnerID: this.partnerId,
        UserID: this.user.ID,
      };
      this.technicianService.saveTechnician(technician).subscribe(
        (technician) => {
          this.user.TechnicianID = technician.ID;
          this.user.Technician = technician;
          this.update.emit(this.user);
          this.messageService.add({
            life: 5000,
            detail: this.translate.instant('messages.technician_linked', {
              name: technician.Firstname,
            }),
          });
          this.isTechnician = true;
          console.log(this.userView);
          if (this.userView === 'technician_view') {
            this.message = this.translate.instant(
              'messages.add_working_hours_reminder',
            );
          }
        },
        (err) => {
          this.error = true;
          this.errorMessage =
            'You have reached you limit for adding technicians. You can make other technician inactive or upgrade your plan';
        },
      );
    }
  }

  delinkTechnician() {
    this.confirmationService.confirm({
      key: 'user_key',
      header: this.translate.instant('general.confirm'),
      acceptLabel: this.translate.instant('general.yes'),
      rejectLabel: this.translate.instant('general.no'),
      message: this.translate.instant('messages.admin_view_confirmation'),
      accept: () => {
        let technician = this.user.Technician;
        technician.UserID = 0;
        this.technicianService
          .saveTechnician(technician)
          .subscribe((result) => {
            delete this.user.Technician;
            this.user.TechnicianID = 0;
            this.update.emit(this.user);
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant('messages.technician_delinked', {
                name: technician.Firstname,
              }),
            });
            this.isTechnician = false;
          });
      },
      reject: () => { },
    });
  }
}
